import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Divider,
  Radio,
  Checkbox,
  InputLabel,
  IconButton,
  Modal,
  Tooltip,
  Backdrop,
  CircularProgress,
  Alert,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import EditIcon from "@mui/icons-material/Edit";

import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import { verificarEtapaAnterior } from "../utils/etapaHelpers";

import useUserRoles from "hooks/useUserRoles";
import useParametrosGenerales from "hooks/useParametrosGenerales";
import useAudienciaEditorImages from "./hooks/useAudienciaEditorImages";

//import ProyeccionModal from "./proyeccion/index";
import ProyeccionModal from "../../registrar/components/FinanzasForm/components/proyeccion/index";
import ProcesosApi from "api/procesosApi";

import ReactQuill from "react-quill";
import styled from "styled-components";
import "react-quill/dist/quill.snow.css";

const StyledEditor = styled(ReactQuill)`
  .ql-toolbar {
    background-color: #f5f5f5;
    border-radius: 8px 8px 0 0;
  }
  .ql-container {
    min-height: 200px;
    font-size: 16px;
    border-radius: 0 0 8px 8px;
    font-family: "Roboto", sans-serif;
  }
  .ql-editor {
    padding: 16px;
    line-height: 1.5;
  }
  .ql-toolbar button:hover {
    background-color: #e0e0e0;
  }
  .ql-editor img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 10px auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ContainerNegociacion = ({
  proceso,
  audiencia: initialAudiencia,
  audienciaAnterior: initialAudienciaAnterior,
  onAudienciaChange,
}) => {
  const { user, roles } = useUserRoles();
  const { parametros } = useParametrosGenerales();
  const [manifestaciones, setManifestaciones] = useState("");
  const quillManifestacionesRef = useRef(null);
  const [deudas, setDeudas] = useState([]);
  const [porcConciliado, setPorcConciliado] = useState(0);
  const [porcNoConciliado, setPorcNoConciliado] = useState(0);
  const [porcSinVoto, setPorcSinVoto] = useState(0);
  const [porcAusente, setPorcAusente] = useState(0);
  const [audiencia, setAudiencia] = useState(initialAudiencia);
  const [audienciaAnterior, setAudienciaAnterior] = useState(initialAudienciaAnterior);
  const [checkedCumplida, setCheckedCumplida] = useState(false);
  const [ordenActual] = useState(5);
  const [verificacion, setVerificacion] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalAcuerdo, setModalAcuerdo] = useState(false);
  const [deudaEditando, setDeudaEditando] = useState(null);
  const [showProyeccion, setShowProyeccion] = useState(false);
  const [showProyeccionAcuerdo, setShowProyeccionAcuerdo] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [propuestaId, setPropuestaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Estados para modal de descripción de propuesta
  const [showDescripcionModal, setShowDescripcionModal] = useState(false);
  const [descripcionGeneral, setDescripcionGeneral] = useState("");
  const [propuestaConciliacion, setPropuestaConciliacion] = useState(null);

  // Hook personalizado para manejo de imágenes específico de audiencias
  const {
    uploadingImage,
    imageError,
    setupImageHandler,
    processImagesForPDF,
    getQuillModules,
    getQuillFormats,
    validateContent,
    cleanOrphanImages,
    clearError,
    resetImageCounters,
  } = useAudienciaEditorImages(proceso?.identificador, initialAudiencia?.id, "negociacion");

  const fetchDatosIniciales = async () => {
    try {
      setLoading(true);
      const [respDeudas] = await Promise.all([ProcesosApi.obtenerDeudasProceso(proceso.id)]);
      const deudasFormateadas = respDeudas.data.deudas.map((deuda) => {
        const { formatted: capiFormatted, value: capiValue } = formatCurrency(
          String(deuda.capital || 0)
        );
        const { formatted: conciliadoFormatted, value: conciliadoValue } = formatCurrency(
          String(deuda.capital_conciliado || 0)
        );
        const { formatted: acuerdoFormatted, value: acuerdoValue } = formatCurrency(
          String(deuda.capital_acuerdo || 0)
        );
        const { formatted: interesesFormatted, value: interesesValue } = formatCurrency(
          String(deuda.intereses || 0)
        );
        const { formatted: inteMoraFormatted, value: inteMoraValue } = formatCurrency(
          String(deuda.intereses_mora || 0)
        );
        const { formatted: otrosFormatted, value: otrosValue } = formatCurrency(
          String(deuda.otros_valores || 0)
        );
        const suma_total = parseInt(interesesValue + inteMoraValue + otrosValue + conciliadoValue);
        const { formatted: sumaFormatted, value: sumaValue } = formatCurrency(
          String(suma_total || 0)
        );
        return {
          ...deuda,
          conciliada: deuda.conciliada || false,
          no_conciliada: deuda.no_conciliada || false,
          capital: capiValue,
          capital_formatted: capiFormatted,
          intereses: interesesValue,
          intereses_formatted: interesesFormatted,
          intereses_mora: inteMoraValue,
          intereses_mora_formatted: inteMoraFormatted,
          otros_valores: otrosValue,
          otros_valores_formatted: otrosFormatted,
          capital_conciliado: conciliadoValue,
          capital_conciliado_formatted: conciliadoFormatted,
          capital_acuerdo: acuerdoValue,
          capital_acuerdo_formatted: acuerdoFormatted,
          suma_total: sumaValue,
          suma_total_formatted: sumaFormatted,
        };
      });
      setDeudas(deudasFormateadas);
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener propuesta de tipo conciliacion
  const fetchPropuestaConciliacion = async () => {
    try {
      const response = await ProcesosApi.obtenerPropuestas(proceso.id, "conciliacion");
      if (response.data && response.data.length > 0) {
        const propuesta = response.data[0]; // La más reciente
        setPropuestaConciliacion(propuesta);
        setDescripcionGeneral(propuesta.descripcion_general || "");
      }
    } catch (error) {
      console.error("Error al obtener propuesta de conciliación:", error);
    }
  };

  // Guardar descripción general de la propuesta
  const handleGuardarDescripcion = async () => {
    if (!propuestaConciliacion) {
      alert("No hay propuesta de conciliación para actualizar.");
      return;
    }
    try {
      setSaving(true);
      await ProcesosApi.actualizarPropuesta(propuestaConciliacion.id, {
        descripcion_general: descripcionGeneral,
      });
      setPropuestaConciliacion((prev) => ({
        ...prev,
        descripcion_general: descripcionGeneral,
      }));
      setShowDescripcionModal(false);
      alert("Descripción actualizada correctamente.");
    } catch (error) {
      console.error("Error al actualizar descripción:", error);
      alert(obtenerMensajeError(error, "Error al actualizar la descripción."));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setAudiencia(initialAudiencia);
    setAudienciaAnterior(initialAudienciaAnterior);
    fetchDatosIniciales();
    fetchPropuestaConciliacion();
    const etapaActual = initialAudiencia?.etapas?.find((etapa) => etapa.orden === ordenActual);
    if (etapaActual?.resultado?.manifestaciones) {
      setManifestaciones(etapaActual.resultado.manifestaciones);
    }
    if (etapaActual?.estado === "CUMPLIDA") setCheckedCumplida(true);
    if (initialAudiencia && initialAudiencia.etapas) {
      setVerificacion(verificarEtapaAnterior(initialAudiencia.etapas, ordenActual));
    }
    // Resetear contadores de imágenes cuando cambie la audiencia
    resetImageCounters();
  }, [user, proceso, initialAudiencia, resetImageCounters]);

  // Configurar manejador de imagen cuando el ref esté listo
  useEffect(() => {
    const timer = setTimeout(() => {
      setupImageHandler(quillManifestacionesRef, "manifestaciones", (imageInfo) => {
        console.log("Imagen subida en manifestaciones:", imageInfo);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [manifestaciones, setupImageHandler]);

  useEffect(() => {
    calcularPorcentajes();
  }, [deudas]);

  const calcularPorcentajes = () => {
    try {
      setLoading(true);
      // Filtrar las deudas que NO están costas
      const deudasNoCostas = deudas.filter(
        (deuda) => deuda.costas !== true || deuda.postergado !== true
      );
      const totalCapitalConciliado = deudasNoCostas.reduce(
        (sum, d) => sum + (d.capital_conciliado || 0),
        0
      );
      // Calcular capital conciliado (deudas conciliadas y no costas)
      const capitalConciliado = deudas
        .filter(
          (deuda) =>
            deuda.conciliada === true && (deuda.costas !== true || deuda.postergado !== true)
        )
        .reduce((sum, d) => sum + (d.capital_conciliado || 0), 0);
      setPorcConciliado(
        totalCapitalConciliado > 0 ? (capitalConciliado / totalCapitalConciliado) * 100 : 0
      );
      // Calcular capital no conciliado (deudas no conciliadas y no costas)
      const capitalNoConciliado = deudas
        .filter(
          (deuda) =>
            deuda.no_conciliada === true && (deuda.costas !== true || deuda.postergado !== true)
        )
        .reduce((sum, d) => sum + (d.capital_conciliado || 0), 0);
      setPorcNoConciliado(
        totalCapitalConciliado > 0 ? (capitalNoConciliado / totalCapitalConciliado) * 100 : 0
      );
      // Calcular capital ausente (deudas con acreedor ausente y no costas)
      const capitalAusente = deudas
        .filter(
          (deuda) =>
            deuda.acreedor_ausente === true && (deuda.costas !== true || deuda.postergado !== true)
        )
        .reduce((sum, d) => sum + (d.capital_conciliado || 0), 0);
      setPorcAusente(
        totalCapitalConciliado > 0 ? (capitalAusente / totalCapitalConciliado) * 100 : 0
      );
      // Calcular capital sin voto (deudas sin estado definido y no costas ni postergado)
      const capitalSinVoto = deudas
        .filter(
          (deuda) =>
            deuda.conciliada === false &&
            deuda.no_conciliada === false &&
            deuda.acreedor_ausente === false &&
            deuda.postergado !== true &&
            deuda.costas !== true
        )
        .reduce((sum, d) => sum + (d.capital_conciliado || 0), 0);
      setPorcSinVoto(
        totalCapitalConciliado > 0 ? (capitalSinVoto / totalCapitalConciliado) * 100 : 0
      );
    } catch (error) {
      console.error("Error al obtener los porcentajes:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const number = value ? value.replace(/\D/g, "") : "0";
    const numberValue = Number(number);
    const formatter = new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return {
      formatted: formatter.format(number || 0).replace(/\s/g, ""),
      value: numberValue,
    };
  };

  const handleConciliacionChange = (id, selectedOption) => {
    setDeudas((prevDeudas) =>
      prevDeudas.map((deuda) => {
        if (deuda.id !== id) return deuda;
        // Resetear todos los estados primero
        deuda.conciliada = false;
        deuda.no_conciliada = false;
        deuda.acreedor_ausente = false;
        // Activar solo la opción seleccionada
        switch (selectedOption) {
          case "conciliada":
            deuda.conciliada = true;
            deuda.no_conciliada = false;
            deuda.acreedor_ausente = false;
            break;
          case "no_conciliada":
            deuda.conciliada = false;
            deuda.no_conciliada = true;
            deuda.acreedor_ausente = false;
            break;
          case "acreedor_ausente":
            deuda.conciliada = false;
            deuda.no_conciliada = false;
            deuda.acreedor_ausente = true;
            break;
          case "sin_voto":
            deuda.conciliada = false;
            deuda.no_conciliada = false;
            deuda.acreedor_ausente = false;
            break;
          default:
            break;
        }
        return {
          ...deuda,
        };
      })
    );
  };

  const handleSave = async () => {
    // Limpiar errores de imagen
    clearError();
    // Validar contenido
    const manifestacionesValidation = validateContent(manifestaciones);
    if (!manifestacionesValidation.isValid) {
      alert("Error en manifestaciones: " + manifestacionesValidation.errors.join(", "));
      return;
    }
    const deudasResultado = deudas.map((deuda) => ({
      id_deuda: deuda.id,
      nombre_acreedor: deuda.str_acreedor.nombre_completo,
      etiqueta: deuda.etiqueta,
      capital: deuda.capital,
      capital_conciliado: deuda.capital_conciliado,
      capital_acuerdo: deuda.capital_acuerdo,
      conciliada: deuda.conciliada,
      no_conciliada: deuda.no_conciliada,
      intereses: deuda.intereses || 0,
      suma_intereses: deuda.suma_intereses,
      postergado: deuda.postergado,
      costas: deuda.costas,
    }));
    const agrupadoPorAcreedor = deudasResultado.reduce((acc, deuda) => {
      const nombre = deuda.nombre_acreedor;
      if (!acc[nombre]) {
        acc[nombre] = [];
      }
      acc[nombre].push(deuda);
      return acc;
    }, {});
    const cantidadPluralidad = Object.values(agrupadoPorAcreedor).filter((deudasAcreedor) =>
      deudasAcreedor.some((d) => d.conciliada === true || d.no_conciliada === true)
    ).length;
    // Umbrales configurables: MIN_PLURALIDAD_ACREEDORES y PORC_VOTACION_ACUERDO
    const minimoPluralidad = parametros.MIN_PLURALIDAD_ACREEDORES;
    const porcentajeAcuerdo = parametros.PORC_VOTACION_ACUERDO;
    const getResultadoTexto = () => {
      // PLURALIDAD cuando la cantidad mínima de Acreedores votaron Positivo ó Negativo
      if (cantidadPluralidad < minimoPluralidad) {
        return "FRACASO - Pluralidad No Alcanzada";
      } else if (porcConciliado >= porcentajeAcuerdo) {
        return "ACUERDO DE PAGO";
      } else if (porcConciliado < porcentajeAcuerdo) {
        return "FRACASO - Votación No Alcanzada";
      }
      return "FRACASO";
    };
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas guardar la Etapa de Negociación? El resultado de la votación es: ${getResultadoTexto()}`
    );
    if (!confirmAction) return;
    try {
      setSaving(true);
      // Procesar imágenes para PDF
      const htmlManifestacionesProcesadas = await processImagesForPDF(manifestaciones);
      await Promise.all(
        deudas.map(async (deuda) => {
          await ProcesosApi.actualizarDeuda(proceso.id, deuda.id, deuda);
        })
      );
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlManifestacionesProcesadas, "text/html");
      const htmlLimpio = doc.body.innerHTML;
      const updatedAudiencia = {
        ...audiencia,
        estado: "REALIZADA",
        etapas: audiencia.etapas.map((etapa) => {
          if (etapa.orden === ordenActual) {
            // NEGOCIACION
            return {
              ...etapa,
              estado: checkedCumplida ? "CUMPLIDA" : "PENDIENTE",
              resultado: {
                resultado: getResultadoTexto(),
                negociaciones: deudasResultado,
                porcPositivo: porcConciliado.toFixed(2),
                porcNegativo: porcNoConciliado.toFixed(2),
                porcAusente: porcAusente.toFixed(2),
                porcSinVoto: porcSinVoto.toFixed(2),
                manifestaciones: htmlLimpio,
              },
            };
          } else if (etapa.orden === 6) {
            // RESULTADO
            return {
              ...etapa,
              estado: "PROGRAMADA",
              resultado: null,
            };
          }
          return etapa; // Retornar sin cambios las demás etapas
        }),
      };
      await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
      // Limpiar imágenes huérfanas después de guardar
      console.log("Iniciando limpieza de imágenes huérfanas después de guardar...");
      try {
        const cleanResult = await cleanOrphanImages({
          manifestaciones: manifestaciones,
        });
        if (cleanResult.success) {
          console.log(`Limpieza completada: ${cleanResult.deleted_count || 0} archivos eliminados`);
          if (cleanResult.deleted_files && cleanResult.deleted_files.length > 0) {
            console.log("Archivos eliminados:", cleanResult.deleted_files);
          }
        } else {
          console.warn("Error en limpieza de imágenes:", cleanResult.error);
        }
      } catch (cleanError) {
        console.error("Error ejecutando limpieza de imágenes:", cleanError);
      }
      setAudiencia(updatedAudiencia);
      onAudienciaChange && onAudienciaChange(updatedAudiencia);
      fetchDatosIniciales();
    } catch (error) {
      console.error("Error al actualizar las deudas o la audiencia:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleAbrirEtapa = async () => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas abrir la Etapa de Negociación?`
    );
    if (!confirmAction) return;
    try {
      setSaving(true);
      const updatedAudiencia = {
        ...audiencia,
        etapas: audiencia.etapas.map((etapa) => {
          if (etapa.orden === ordenActual) {
            return {
              ...etapa,
              estado: "PENDIENTE",
            };
          } else if (etapa.orden > ordenActual) {
            return {
              ...etapa,
              estado: "PROGRAMADA",
            };
          }
          return etapa;
        }),
      };
      await ProcesosApi.actualizarAudiencia(audiencia.id, updatedAudiencia);
      setAudiencia(updatedAudiencia);
      onAudienciaChange && onAudienciaChange(updatedAudiencia);
      fetchDatosIniciales();
    } catch (error) {
      console.error("Error al abrir la Etapa", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditorManifestacionesChange = useCallback(
    (value) => {
      setManifestaciones(value);
      clearError(); // Limpiar errores cuando el usuario escriba
    },
    [clearError]
  );

  // ... resto de funciones sin cambios ...

  const deudasAgrupadasPorClase = deudas.reduce((acc, deuda) => {
    const clase = deuda.str_clase_deuda.nombre || "Sin Clase";
    if (!acc[clase]) {
      acc[clase] = {
        deudas: [],
        totalCapital: 0,
        totalCapitalConciliado: 0,
        totalCapitalAcuerdo: 0,
        totalSumaTotal: 0,
      };
    }
    acc[clase].deudas.push(deuda);
    acc[clase].totalCapital += deuda.capital || 0;
    acc[clase].totalCapitalAcuerdo += deuda.capital_acuerdo || 0;
    acc[clase].totalSumaTotal += deuda.suma_total || 0;
    //acc[clase].totalCapitalConciliado += deuda.capital_conciliado || 0;
    if (!deuda.costas || !deuda.postergado) {
      acc[clase].totalCapitalConciliado += deuda.capital_conciliado || 0;
    }
    return acc;
  }, {});

  const totalGeneralCapital = deudas.reduce((sum, deuda) => sum + (deuda.capital || 0), 0);
  const totalGeneralAcuerdo = deudas.reduce((sum, deuda) => sum + (deuda.capital_acuerdo || 0), 0);
  const totalGeneralCapitalConciliado = deudas
    .filter((deuda) => !deuda.costas || !deuda.postergado)
    .reduce((sum, deuda) => sum + (deuda.capital_conciliado || 0), 0);

  deudas.forEach((deuda) => {
    deuda.participacion =
      totalGeneralCapital > 0
        ? (((deuda.capital || 0) / totalGeneralCapital) * 100).toFixed(2)
        : "0.00";
    deuda.participacion_acuerdo =
      totalGeneralCapital > 0
        ? (((deuda.capital_acuerdo || 0) / totalGeneralAcuerdo) * 100).toFixed(2)
        : "0.00";
    if (deuda.costas || deuda.postergado) {
      deuda.derecho_voto = "0.00";
    } else {
      deuda.derecho_voto =
        totalGeneralCapitalConciliado > 0
          ? (((deuda.capital_conciliado || 0) / totalGeneralCapitalConciliado) * 100).toFixed(2)
          : "0.00";
    }
  });

  Object.values(deudasAgrupadasPorClase).forEach((grupo) => {
    grupo.participacion =
      totalGeneralCapital > 0
        ? ((grupo.totalCapital / totalGeneralCapital) * 100).toFixed(2)
        : "0.00";
    grupo.participacion_acuerdo =
      totalGeneralAcuerdo > 0
        ? (((grupo.totalCapitalAcuerdo || 0) / totalGeneralAcuerdo) * 100).toFixed(2)
        : "0.00";
    grupo.derecho_voto =
      totalGeneralCapitalConciliado > 0
        ? (((grupo.totalCapitalConciliado || 0) / totalGeneralCapitalConciliado) * 100).toFixed(2)
        : "0.00";
  });

  const headerStyles = (color) => ({
    fontWeight: "bold",
    padding: "6px 8px",
    backgroundColor: "#f5f5f5",
    border: "1px solid #ddd",
    textAlign: "center",
    fontSize: "0.875rem",
    color: color,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    boxShadow: "0 2px 3px rgba(0,0,0,0.1)",
  });

  const abrirModalEdicion = (deuda) => {
    setDeudaEditando(deuda);
    setModalAbierto(true);
  };

  const abrirModalEdicionAcuerdo = (deuda) => {
    setDeudaEditando(deuda);
    setModalAcuerdo(true);
  };

  const handleCambioValor = (field) => (event) => {
    const { formatted, value } = formatCurrency(event.target.value);
    setDeudaEditando((prev) => ({
      ...prev,
      [field]: value,
      [`${field}_formatted`]: formatted,
    }));
  };

  const guardarCambios = async () => {
    //alert("deudaEditando: " + JSON.stringify(deudaEditando));
    await ProcesosApi.actualizarDeuda(deudaEditando.proceso, deudaEditando.id, deudaEditando);
    setModalAbierto(false);
    setModalAcuerdo(false);
    fetchDatosIniciales();
    alert("La Acreencia ha sido actualizada satisfactoriamente.");
  };

  const isDisabled = () => {
    return (
      !verificacion ||
      initialAudiencia.estado === "SUSPENDIDA" ||
      initialAudiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.estadoActual === "CUMPLIDA" ||
      saving ||
      uploadingImage // DESHABILITAR MIENTRAS SE SUBE IMAGEN
    );
  };

  const isOpenEtapaDisabled = () => {
    return (
      !verificacion ||
      initialAudiencia.estado === "SUSPENDIDA" ||
      initialAudiencia.estado === "CERRADA" ||
      (ordenActual > 1 && !verificacion.isQuorumCumplido) ||
      (ordenActual > 1 && !verificacion.isEtapaAnteriorCumplida) ||
      verificacion.isEtapaActualPendiente ||
      verificacion.isEtapaActualProgramada ||
      verificacion.isEtapaSiguienteCumplida ||
      saving ||
      uploadingImage // DESHABILITAR MIENTRAS SE SUBE IMAGEN
    );
  };

  const getTooltipTitle = (deuda) => {
    if (deuda.costas && deuda.postergado) return "Costas y Postergado";
    if (deuda.costas) return "Costas";
    if (deuda.postergado) return "Postergado";
    return null;
  };

  return (
    <Container>
      {(loading || saving || uploadingImage) && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
              {uploadingImage ? "Subiendo imagen..." : saving ? "Guardando..." : "Cargando..."}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {imageError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {imageError}
        </Alert>
      )}

      {proceso && audiencia ? (
        <>
          {/* Sección de porcentajes */}
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table size="small">
                  <TableRow sx={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#eb984e" }}>Acreencias</span>
                      <br />
                      <span style={{ color: "#eb984e" }}>
                        {formatCurrency(String(totalGeneralCapital)).formatted}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#1976d2" }}>Conciliado</span>
                      <br />
                      <span style={{ color: "#1976d2" }}>
                        {formatCurrency(String(totalGeneralCapitalConciliado)).formatted}
                      </span>
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        textAlign: "center",
                        fontSize: "1.2rem",
                      }}
                    >
                      <SuiButton
                        variant="gradient"
                        buttonColor="dark"
                        onClick={() => setShowProyeccion(true)}
                        disabled={isDisabled()}
                        sx={{
                          mr: 1,
                          minWidth: "150px",
                          transition: "all 0.3s",
                        }}
                      >
                        Proyección Conciliación
                      </SuiButton>
                      <Tooltip title="Editar descripción de la propuesta">
                        <span>
                          <IconButton
                            onClick={() => setShowDescripcionModal(true)}
                            disabled={isDisabled() || !propuestaConciliacion}
                            sx={{
                              mr: 1,
                              backgroundColor: "#FFD700",
                              color: "#000",
                              "&:hover": {
                                backgroundColor: "#FFC107",
                              },
                              "&:disabled": {
                                backgroundColor: "#e0e0e0",
                              },
                              width: 40,
                              height: 40,
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {audiencia.id != audienciaAnterior?.id &&
                        audienciaAnterior?.resultado === "proceso_en_acuerdo" && (
                          <SuiButton
                            variant="gradient"
                            buttonColor="info"
                            onClick={() => setShowProyeccionAcuerdo(true)}
                            disabled={isDisabled()}
                            sx={{
                              minWidth: "150px",
                              transition: "all 0.3s",
                            }}
                          >
                            Proyección Acuerdo
                          </SuiButton>
                        )}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#4CAF50" }}>% Positivo</span>
                      <br />
                      <span style={{ color: "#4CAF50" }}>{porcConciliado.toFixed(2)} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#e74c3c" }}>% Negativo</span>
                      <br />
                      <span style={{ color: "#e74c3c" }}>{porcNoConciliado.toFixed(2)} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span>% Ausente</span>
                      <br />
                      <span>{porcAusente.toFixed(2)} %</span>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        padding: "4px 8px",
                        color: "white",
                        textAlign: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      <span style={{ color: "#666" }}>% Sin Voto</span>
                      <br />
                      <span style={{ color: "#666" }}>{porcSinVoto.toFixed(2)} %</span>
                    </TableCell>
                  </TableRow>
                </Table>
              </TableContainer>
            </Grid>
          </Box>

          {/* Tablas de deudas agrupadas por clase */}
          {Object.entries(deudasAgrupadasPorClase).map(([clase, data]) => {
            return (
              <Box key={clase} sx={{ mt: 1 }}>
                <SuiTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  {clase}
                </SuiTypography>
                <Grid container spacing={1}>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableRow>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Acreedor
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#eb984e" }}>Acreencia</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Coef.
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          <span style={{ color: "#1976d2" }}>Conciliado</span>
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          DV
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                        >
                          Total
                        </TableCell>
                        {audiencia.id != audienciaAnterior?.id &&
                          audienciaAnterior?.resultado === "proceso_en_acuerdo" && (
                            <TableCell
                              sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                            >
                              <span style={{ color: "#239b56" }}>Acordado</span>
                            </TableCell>
                          )}
                        <TableCell sx={headerStyles("#4CAF50")}>Pos.</TableCell>
                        <TableCell sx={headerStyles("#d32f2f")}>Neg.</TableCell>
                        <TableCell sx={headerStyles("#000")}>Aus.</TableCell>
                        <TableCell sx={headerStyles("#666")}>SV</TableCell>
                      </TableRow>
                      <TableBody>
                        {data.deudas.map((deuda) => {
                          return (
                            <Tooltip key={deuda.id} title={getTooltipTitle(deuda)} placement="left">
                              <TableRow>
                                <TableCell sx={{ padding: "2px 2px" }}>
                                  {deuda.str_acreedor.nombre}
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                  <Tooltip title={deuda.etiqueta} placement="left">
                                    <span style={{ color: "#eb984e" }}>
                                      {deuda.capital_formatted}
                                    </span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                  {deuda.participacion} %
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                  <span style={{ color: "#1976d2" }}>
                                    {deuda.capital_conciliado_formatted}
                                  </span>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                  {deuda.derecho_voto} %
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                  {deuda.suma_total_formatted}
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => abrirModalEdicion(deuda)}
                                    disabled={isDisabled()}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                                {audiencia.id != audienciaAnterior?.id &&
                                  audienciaAnterior?.resultado === "proceso_en_acuerdo" && (
                                    <TableCell sx={{ textAlign: "center", padding: "1px 1px" }}>
                                      <span style={{ color: "#239b56" }}>
                                        {deuda.capital_acuerdo_formatted}
                                      </span>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => abrirModalEdicionAcuerdo(deuda)}
                                        disabled={isDisabled()}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                <TableCell sx={{ padding: "1px 1px", textAlign: "center" }}>
                                  <Radio
                                    checked={!!deuda.conciliada}
                                    onChange={() =>
                                      handleConciliacionChange(deuda.id, "conciliada")
                                    }
                                    disabled={deuda.costas || isDisabled()}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: "1px 1px", textAlign: "center" }}>
                                  <Radio
                                    checked={!!deuda.no_conciliada}
                                    onChange={() =>
                                      handleConciliacionChange(deuda.id, "no_conciliada")
                                    }
                                    disabled={deuda.costas || isDisabled()}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: "1px 1px", textAlign: "center" }}>
                                  <Radio
                                    checked={!!deuda.acreedor_ausente}
                                    onChange={() =>
                                      handleConciliacionChange(deuda.id, "acreedor_ausente")
                                    }
                                    disabled={deuda.costas || isDisabled()}
                                    color="primary"
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: "1px 1px", textAlign: "center" }}>
                                  <Radio
                                    checked={
                                      !deuda.conciliada &&
                                      !deuda.no_conciliada &&
                                      !deuda.acreedor_ausente
                                    }
                                    onChange={() => handleConciliacionChange(deuda.id, "sin_voto")}
                                    disabled={deuda.costas || isDisabled()}
                                    color="primary"
                                  />
                                </TableCell>
                              </TableRow>
                            </Tooltip>
                          );
                        })}
                        {/* Mostrar total por clase */}
                        <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}>
                            TOTAL {clase}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "2px 2px",
                              color: "#eb984e",
                            }}
                          >
                            {formatCurrency(String(data.totalCapital)).formatted}
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            {data.participacion} %
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "2px 2px",
                              color: "#1976d2",
                            }}
                          >
                            {formatCurrency(String(data.totalCapitalConciliado)).formatted}
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: "center", fontWeight: "bold", padding: "2px 2px" }}
                          >
                            {data.derecho_voto} %
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              padding: "2px 2px",
                            }}
                          >
                            {formatCurrency(String(data.totalSumaTotal)).formatted}
                          </TableCell>
                          {audiencia.id != audienciaAnterior?.id &&
                            audienciaAnterior?.resultado === "proceso_en_acuerdo" && (
                              <TableCell
                                sx={{
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  padding: "2px 2px",
                                  color: "#239b56",
                                }}
                              >
                                {formatCurrency(String(data.totalCapitalAcuerdo)).formatted}
                              </TableCell>
                            )}
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                          <TableCell sx={{ fontWeight: "bold", padding: "2px 2px" }}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Divider sx={{ borderColor: "gray", borderStyle: "groove" }} />
              </Box>
            );
          })}

          {/* Sección de manifestaciones con editor enriquecido */}
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <SuiTypography variant="h6" fontWeight="bold">
                Manifestaciones de los Acreedores
              </SuiTypography>
              {uploadingImage && (
                <Typography variant="caption" color="primary">
                  Subiendo imagen...
                </Typography>
              )}
            </Grid>
            <Grid size={12}>
              <StyledEditor
                ref={quillManifestacionesRef}
                value={manifestaciones}
                onChange={handleEditorManifestacionesChange}
                modules={getQuillModules()}
                formats={getQuillFormats()}
                readOnly={isDisabled()}
                placeholder="Ingrese las manifestaciones de la negociación. Puede insertar imágenes usando el botón de imagen en la barra de herramientas."
              />
            </Grid>
          </Grid>

          {/* Botones de guardado y control */}
          {(roles.isAdmin || roles.isOperador) && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={8} sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <InputLabel sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={checkedCumplida}
                    disabled={isDisabled()}
                    onChange={(e) => setCheckedCumplida(e.target.checked)}
                    size="small"
                    sx={{
                      color: "white",
                      "&.Mui-checked": {
                        color: "white",
                      },
                      padding: "4px",
                    }}
                  />
                  <SuiTypography variant="h6" fontWeight="light">
                    ¿Cumplida?
                  </SuiTypography>
                </InputLabel>
                <SuiButton
                  variant="gradient"
                  buttonColor="info"
                  onClick={handleSave}
                  disabled={isDisabled()}
                >
                  Guardar Etapa
                </SuiButton>
                <SuiTypography variant="caption" fontWeight="bold">
                  {!verificacion
                    ? "Cargando verificación..."
                    : audiencia.estado === "SUSPENDIDA"
                    ? "- Audiencia suspendida (no modificable)"
                    : !verificacion.isQuorumCumplido && ordenActual > 1
                    ? "- No se puede avanzar: Quórum no completado"
                    : !verificacion.isEtapaAnteriorCumplida && ordenActual > 1
                    ? "- Complete la etapa anterior primero"
                    : !verificacion.isEtapaActualPendiente && !verificacion.isEtapaActualProgramada
                    ? "- Esta etapa ya fue completada"
                    : ordenActual === 1 && verificacion.estadoActual !== "CUMPLIDA"
                    ? "- Quórum pendiente de completar"
                    : uploadingImage
                    ? "- Subiendo imagen..."
                    : null}
                </SuiTypography>
              </Grid>
              <Grid
                size={4}
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={handleAbrirEtapa}
                  disabled={isOpenEtapaDisabled()}
                >
                  Abrir Etapa
                </SuiButton>
              </Grid>
            </Grid>
          )}
        </>
      ) : (
        <Typography>Cargando...</Typography>
      )}

      {/* Modales de proyección */}
      <ProyeccionModal
        open={showProyeccion}
        onClose={() => setShowProyeccion(false)}
        procesoId={proceso.id}
        disponible={proceso.compromiso_mensual || 5000000}
        propuestaId={propuestaId}
        setPropuestaId={setPropuestaId}
        tipo_propuesta="conciliacion"
        onComplete={(data) => {
          console.log("Proyección de Conciliación completada:", data);
        }}
        onError={(error) => {
          console.error("Error en Proyección de Conciliación:", error);
          alert("Error al generar la Proyección de Conciliación: " + error);
        }}
      />

      <ProyeccionModal
        open={showProyeccionAcuerdo}
        onClose={() => setShowProyeccionAcuerdo(false)}
        procesoId={proceso.id}
        disponible={proceso.compromiso_mensual || 5000000}
        propuestaId={propuestaId}
        setPropuestaId={setPropuestaId}
        tipo_propuesta="acuerdo"
        onComplete={(data) => {
          console.log("Proyección de Acuerdo completada:", data);
        }}
        onError={(error) => {
          console.error("Error al generar la Proyección de Acuerdo:", error);
          alert("Error al generar la Proyección de Acuerdo: " + error);
        }}
      />

      {/* Modal para editar descripción de propuesta */}
      <Modal
        open={showDescripcionModal}
        onClose={() => setShowDescripcionModal(false)}
        aria-labelledby="modal-descripcion-propuesta"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <SuiTypography variant="h5" fontWeight="bold" mb={2}>
            Descripción General de la Propuesta de Conciliación
          </SuiTypography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={descripcionGeneral}
            onChange={(e) => setDescripcionGeneral(e.target.value)}
            placeholder="Ingrese la descripción general de la propuesta de pago..."
            variant="outlined"
            InputProps={{
              sx: {
                alignItems: "flex-start",
              },
            }}
            sx={{
              "& .MuiInputBase-root": {
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                overflow: "auto",
              },
            }}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <SuiButton
              variant="outlined"
              buttonColor="dark"
              onClick={() => setShowDescripcionModal(false)}
            >
              Cancelar
            </SuiButton>
            <SuiButton
              variant="gradient"
              buttonColor="info"
              onClick={handleGuardarDescripcion}
              disabled={saving}
            >
              Guardar
            </SuiButton>
          </Box>
        </Box>
      </Modal>

      {/* Modales de edición */}
      <Modal
        open={modalAbierto}
        onClose={() => setModalAbierto(false)}
        aria-labelledby="modal-editar-deuda"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Editar Deuda
          </Typography>
          <Grid container spacing={2}>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Capital Conciliado
              </SuiTypography>
              <SuiInput
                value={deudaEditando?.capital_conciliado_formatted}
                onChange={handleCambioValor("capital_conciliado")}
                fullWidth
              />
            </Grid>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Intereses
              </SuiTypography>
              <SuiInput
                value={deudaEditando?.intereses_formatted}
                onChange={handleCambioValor("intereses")}
                fullWidth
              />
            </Grid>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Intereses Mora
              </SuiTypography>
              <SuiInput
                value={deudaEditando?.intereses_mora_formatted}
                onChange={handleCambioValor("intereses_mora")}
                fullWidth
              />
            </Grid>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Otros Valores
              </SuiTypography>
              <SuiInput
                value={deudaEditando?.otros_valores_formatted}
                onChange={handleCambioValor("otros_valores")}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} p={2}>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                <Checkbox
                  checked={deudaEditando?.postergado}
                  onChange={(e) => {
                    setDeudaEditando((prev) => ({
                      ...prev,
                      postergado: e.target.checked,
                    }));
                  }}
                />
                {"  "}
                ¿Postergado?
              </SuiTypography>
            </Grid>
            <Grid item size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                <Checkbox
                  checked={deudaEditando?.costas}
                  onChange={(e) => {
                    setDeudaEditando((prev) => ({
                      ...prev,
                      costas: e.target.checked,
                    }));
                  }}
                />
                {"  "}
                ¿Costas?
              </SuiTypography>
            </Grid>
            <Grid item size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                <Checkbox
                  checked={deudaEditando?.suma_intereses}
                  onChange={(e) => {
                    setDeudaEditando((prev) => ({
                      ...prev,
                      suma_intereses: e.target.checked,
                    }));
                  }}
                />
                {"  "}
                ¿Sumar los Intereses al Capital Conciliado para la Proyección de Pago?
              </SuiTypography>
            </Grid>
            <Grid item size={3}></Grid>
          </Grid>

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <SuiButton variant="outlined" onClick={() => setModalAbierto(false)}>
              Cancelar
            </SuiButton>
            <SuiButton variant="gradient" buttonColor="dark" onClick={guardarCambios}>
              Actualizar
            </SuiButton>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={modalAcuerdo}
        onClose={() => setModalAcuerdo(false)}
        aria-labelledby="modal-editar-deuda"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Editar Deuda
          </Typography>
          <Grid container spacing={2} p={2}>
            <Grid item size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Capital Acordado
              </SuiTypography>
              <SuiInput
                value={deudaEditando?.capital_acuerdo_formatted}
                onChange={handleCambioValor("capital_acuerdo")}
                fullWidth
              />
            </Grid>
            <Grid item size={6}></Grid>
          </Grid>

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <SuiButton variant="outlined" onClick={() => setModalAcuerdo(false)}>
              Cancelar
            </SuiButton>
            <SuiButton variant="gradient" buttonColor="dark" onClick={guardarCambios}>
              Actualizar
            </SuiButton>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

ContainerNegociacion.propTypes = {
  proceso: PropTypes.object,
  audiencia: PropTypes.object,
  audienciaAnterior: PropTypes.object,
  onAudienciaChange: PropTypes.func.isRequired,
};

export default ContainerNegociacion;
